import React, { useState } from 'react';
import { authFetch, apiUrl, authService } from '../utils/api';

const RefreshTokenTestPage: React.FC = () => {
  // This is a test page for refresh token functionality.
  const [testResult, setTestResult] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [testSteps, setTestSteps] = useState<string[]>([]);

  const addTestStep = (step: string) => {
    setTestSteps(prev => [...prev, `${new Date().toLocaleTimeString()}: ${step}`]);
  };

  const handleTestRefreshToken = async () => {
    setLoading(true);
    setTestSteps([]);
    setTestResult('');
    addTestStep('Starting refresh token test...');

    let originalToken: string | null = null;
    let originalUserData: any = null;

    try {
      // Step 1: Make an initial authenticated request that we expect to fail with 401
      // To force a 401, we will temporarily invalidate the token in localStorage.
      originalToken = authService.getToken();
      originalUserData = authService.getUserData();
      
      if (originalToken) {
        addTestStep('Temporarily invalidating token in localStorage to force 401...');
        localStorage.setItem('authData', JSON.stringify({ token: 'invalid_token', userData: originalUserData }));
      }

      addTestStep('Attempting an authenticated API call...');
      const response = await authFetch(apiUrl('Course/all?pageNumber=1&pageSize=1'), { 
        method: 'GET',
      }, false); // Set retry to false for the initial explicit 401 test

      if (response.ok) {
        addTestStep('Initial request succeeded unexpectedly. Refresh token might not have been triggered.');
        setTestResult('Test Result: Initial request succeeded (expected 401). Test might be invalid.');
      } else if (response.status === 401) {
        addTestStep('Initial request returned 401 as expected. Refresh token flow should initiate.');
        
        // Make a second call, this time allowing authFetch to retry
        addTestStep('Making a second call, allowing authFetch to handle refresh and retry...');
        const retryResponse = await authFetch(apiUrl('Course/all?pageNumber=1&pageSize=1'), { 
          method: 'GET',
        }, true); // Allow retry
        
        if (retryResponse.ok) {
          addTestStep('Second request (retried) succeeded with 200. Refresh token flow successful!');
          setTestResult('Test Result: SUCCESS! Refresh token flow worked as expected.');
        } else {
          addTestStep(`Second request (retried) failed with status: ${retryResponse.status}. Refresh token flow failed.`);
          setTestResult(`Test Result: FAILED! Second request failed with status ${retryResponse.status}.`);
        }

      } else {
        addTestStep(`Initial request failed with unexpected status: ${response.status}.`);
        setTestResult(`Test Result: FAILED! Unexpected status ${response.status} for initial request.`);
      }
      
    } catch (error: any) {
      addTestStep(`Error during test: ${error.message}`);
      setTestResult(`Test Result: Error - ${error.message}`);
      console.error('Refresh Token Test Error:', error);
    } finally {
      addTestStep('Test completed.');
      setLoading(false);
      // Ensure the original token is restored if it was temporarily invalidated
      const storedAuth = localStorage.getItem('authData');
      if (storedAuth && JSON.parse(storedAuth).token === 'invalid_token' && originalToken) {
        localStorage.setItem('authData', JSON.stringify({ token: originalToken, userData: originalUserData }));
        addTestStep('Restored original token in localStorage.');
      } else if (!storedAuth && originalToken) {
         localStorage.setItem('authData', JSON.stringify({ token: originalToken, userData: originalUserData }));
         addTestStep('Restored original token in localStorage (was missing).');
      }
    }
  };

  const clearResults = () => {
    setTestResult('');
    setTestSteps([]);
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">Refresh Token Test Page</h2>
      
      <div className="space-y-4">
        <div className="flex gap-4">
          <button
            onClick={handleTestRefreshToken}
            disabled={loading}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
          >
            {loading ? 'Testing...' : 'Run Refresh Token Test'}
          </button>

          <button
            onClick={clearResults}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Clear Results
          </button>
        </div>

        {testSteps.length > 0 && (
          <div className="mt-4">
            <h3 className="text-lg font-semibold mb-2">Test Steps:</h3>
            <div className="bg-gray-100 p-4 rounded overflow-auto max-h-96 whitespace-pre-wrap">
              {testSteps.map((step, index) => (
                <div key={index} className="mb-1">
                  {step}
                </div>
              ))}
            </div>
          </div>
        )}

        {testResult && (
          <div className="mt-4">
            <h3 className="text-lg font-semibold mb-2">Test Result:</h3>
            <pre className="bg-gray-100 p-4 rounded overflow-auto max-h-96 whitespace-pre-wrap">
              {testResult}
            </pre>
          </div>
        )}

        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
          <h3 className="text-lg font-semibold mb-2">How to Test:</h3>
          <ol className="list-decimal list-inside space-y-2">
            <li>Make sure you're logged in to the application.</li>
            <li>Open your browser's Developer Tools (F12) and go to the <span className="font-semibold">Network tab</span>.</li>
            <li>Click the "Run Refresh Token Test" button.</li>
            <li>Watch the Network tab for the following sequence:
              <ul className="list-disc list-inside ml-4 mt-1">
                <li>An initial API request (e.g., <span className="font-mono">Course/all</span>) that will likely show a <span className="font-semibold">401 Unauthorized</span> response (because the token is temporarily invalidated).</li>
                <li>Immediately following, a <span className="font-mono">POST</span> request to <span className="font-mono">/ModeratorAuth/refresh-token</span> (this is your refresh token API being called automatically).</li>
                <li>Finally, the <span className="font-mono">original API request will be retried</span> and should succeed with a <span className="font-semibold">200 OK</span> response.</li>
              </ul>
            </li>
            <li>Check the "Test Steps" and "Test Result" sections on this page for a summary.</li>
          </ol>
          <p className="mt-2 text-sm text-gray-600">Note: This test temporarily invalidates your access token in localStorage to simulate an expired token. Your actual session should be restored after the test completes.</p>
        </div>
      </div>
    </div>
  );
};

export default RefreshTokenTestPage; 